import React from "react";
import {AccordionItem} from "../../components";
import {Col, Container, Row} from "react-grid-system";
import {EstEstateTaxHeader} from "./EstEstateTaxHeader";
import AccordionWithHeader from "../../components/Accordion/AccordionWithHeader";
import {COLOR_TAXES} from "../../constants/colors";
import {EstateTaxBreakdown, StrategyDetailsSectionInfo} from "../WealthPOTypes";
import {EstateTaxBreakdownAccordionHeader} from "./EstateTaxBreakdownAccordionHeader";
import {EstateTaxRunningTotal, GTGTExemption} from "../../WealthTransfer/components/EstateTaxRunningTotal";
import {
    EstateTaxBreakdownReportRowProps
} from "../../Reporting/DetailedEmailReports/WealthPlanOptimizerReport/EstateTaxBreakdownReportRow";
import {
    EstateTaxRunningTotalsReport
} from "../../Reporting/DetailedEmailReports/WealthPlanOptimizerReport/EstateTaxRunningTotalsReport";
import classNames from "classnames";


export type EstateTaxBreakdownReportContent = {
    isContinued: boolean;
    showStateTaxBreakdown: boolean;
    showFederalTaxBreakdown: boolean;
    stateTaxBreakdownRows: EstateTaxBreakdownReportRowProps[];
    federalTaxBreakdownRows: EstateTaxBreakdownReportRowProps[];
}

interface EstateTaxBreakdownProps {
    estateTaxBreakdownWithoutStrategies: EstateTaxBreakdown;
    estateTaxBreakdownWithStrategies: EstateTaxBreakdown;
    strategyDetailsList?: StrategyDetailsSectionInfo[];
    sorDescription?: string;
    gtgtExemptions?: GTGTExemption[];
    isInReportPreview?: boolean;
    estateTaxBreakdownReportContent?: EstateTaxBreakdownReportContent
}

const EstateTaxBreakdownContent: React.FC<EstateTaxBreakdownProps> = ({
                                                                          estateTaxBreakdownWithoutStrategies,
                                                                          estateTaxBreakdownWithStrategies,
                                                                          strategyDetailsList,
                                                                          sorDescription,
                                                                          gtgtExemptions,
                                                                          isInReportPreview,
                                                                          estateTaxBreakdownReportContent
                                                                      }: EstateTaxBreakdownProps) => {
    return (
        <Container className={classNames("est-estate-tax-card",
            {"reporting-summary-details-card": isInReportPreview}
        )} data-testid="estate-tax-breakdown-content">
            <Row>
                <Col>
                    <h4>{estateTaxBreakdownReportContent?.isContinued
                        ? "Estimated Estate Tax Breakdown (continued)"
                        : "Estimated Estate Tax Breakdown"}</h4>
                </Col>
            </Row>

            <AccordionWithHeader
                accordionId={"est-estate-tax-accordion"}
                allItems={[]}
                columnTitles={["", "Without Strategies", "With Strategies", ""]}
                renderHeader={true}
                HeaderComponent={() => EstEstateTaxHeader()}
            >
                <AccordionItem
                    uuid="estate-tax-breakdown"
                    primaryText="Estimated Estate Tax"
                    accentColor={COLOR_TAXES}
                    expandedAccordionItems={isInReportPreview ? ["estate-tax-breakdown"] : []}
                    HeaderComponent={({expanded}) => EstateTaxBreakdownAccordionHeader(
                        {
                            estateTaxWithoutStrategies: estateTaxBreakdownWithoutStrategies.estimatedEstateTax,
                            estateTaxWithStrategies: estateTaxBreakdownWithStrategies.estimatedEstateTax,
                            isContinued: estateTaxBreakdownReportContent?.isContinued ?? false,
                            expanded
                        }
                    )}
                >
                    {!isInReportPreview && <>
                        <EstateTaxRunningTotal
                        taxType={"State"}
                        runningTotalAmounts={{
                            estateValue: estateTaxBreakdownWithoutStrategies.estateValue,
                            effectiveTaxRate: estateTaxBreakdownWithoutStrategies.stateTaxSummary.effectiveTaxRate,
                            estimatedEstateTax: estateTaxBreakdownWithoutStrategies.stateTaxSummary.estimatedEstateTax,
                            taxableEstateValue: estateTaxBreakdownWithoutStrategies.stateTaxSummary.taxableEstateValue
                        }}
                        runningTotalAmountsWithStrategies={{
                            estateValue: estateTaxBreakdownWithStrategies.estateValue,
                            effectiveTaxRate: estateTaxBreakdownWithStrategies.stateTaxSummary.effectiveTaxRate,
                            estimatedEstateTax: estateTaxBreakdownWithStrategies.stateTaxSummary.estimatedEstateTax,
                            taxableEstateValue: estateTaxBreakdownWithStrategies.stateTaxSummary.taxableEstateValue
                        }}
                        charitableDeduction={estateTaxBreakdownWithoutStrategies.charitableDeduction}
                        additionalExemptionsOrDeductions={[{
                            label: "State Exemption",
                            amount: estateTaxBreakdownWithoutStrategies.stateTaxSummary.totalAvailableExemption,
                            amountWithStrategies: estateTaxBreakdownWithStrategies.stateTaxSummary.totalAvailableExemption,
                        }]}
                        strategyDetailsList={strategyDetailsList}
                        sorDescription={sorDescription}/>
                        <EstateTaxRunningTotal
                        taxType={"Federal"}
                        runningTotalAmounts={{
                            estateValue: estateTaxBreakdownWithoutStrategies.estateValue,
                            effectiveTaxRate: estateTaxBreakdownWithoutStrategies.federalTaxSummary.effectiveTaxRate,
                            estimatedEstateTax: estateTaxBreakdownWithoutStrategies.federalTaxSummary.estimatedEstateTax,
                            taxableEstateValue: estateTaxBreakdownWithoutStrategies.federalTaxSummary.taxableEstateValue
                        }}
                        runningTotalAmountsWithStrategies={{
                            estateValue: estateTaxBreakdownWithStrategies.estateValue,
                            effectiveTaxRate: estateTaxBreakdownWithStrategies.federalTaxSummary.effectiveTaxRate,
                            estimatedEstateTax: estateTaxBreakdownWithStrategies.federalTaxSummary.estimatedEstateTax,
                            taxableEstateValue: estateTaxBreakdownWithStrategies.federalTaxSummary.taxableEstateValue
                        }}
                        charitableDeduction={estateTaxBreakdownWithoutStrategies.charitableDeduction}
                        additionalExemptionsOrDeductions={[{
                            label: "State Estate Tax Deduction",
                            amount: estateTaxBreakdownWithoutStrategies.stateTaxSummary.estimatedEstateTax,
                            amountWithStrategies: estateTaxBreakdownWithStrategies.stateTaxSummary.estimatedEstateTax,
                        }, {
                            label: "Available Exemption",
                            amount: estateTaxBreakdownWithoutStrategies.federalTaxSummary.totalAvailableExemption,
                            amountWithStrategies: estateTaxBreakdownWithStrategies.federalTaxSummary.totalAvailableExemption,
                        }]}
                        strategyDetailsList={strategyDetailsList}
                        gtgtExemptions={gtgtExemptions}
                        />
                    </>}

                    {isInReportPreview && <>
                        {estateTaxBreakdownReportContent?.showStateTaxBreakdown && <EstateTaxRunningTotalsReport
                            estateTaxBreakdownRows={estateTaxBreakdownReportContent.stateTaxBreakdownRows}
                            headerTaxWithoutStrategies={estateTaxBreakdownWithoutStrategies.stateTaxSummary.estimatedEstateTax}
                            headerTaxWithStrategies={estateTaxBreakdownWithStrategies.stateTaxSummary.estimatedEstateTax}
                            isContinued={estateTaxBreakdownReportContent.isContinued}
                            taxType={"State"}/>}
                        {estateTaxBreakdownReportContent?.showFederalTaxBreakdown && <EstateTaxRunningTotalsReport
                            estateTaxBreakdownRows={estateTaxBreakdownReportContent.federalTaxBreakdownRows}
                            headerTaxWithoutStrategies={estateTaxBreakdownWithoutStrategies.federalTaxSummary.estimatedEstateTax}
                            headerTaxWithStrategies={estateTaxBreakdownWithStrategies.federalTaxSummary.estimatedEstateTax}
                            isContinued={!estateTaxBreakdownReportContent?.showStateTaxBreakdown && estateTaxBreakdownReportContent.isContinued}
                            taxType={"Federal"}/>}
                    </>}
                </AccordionItem>
            </AccordionWithHeader>
        </Container>
    );
};

export default EstateTaxBreakdownContent;
